import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion } from 'framer-motion';
import { Eye, EyeOff, Lock, CheckCircle } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { validatePasswordStrength } from '../utils/passwordValidation';
import PasswordStrengthIndicator from '../components/auth/PasswordStrengthIndicator';
import toast from 'react-hot-toast';

export default function UpdatePassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [isInviteFlow, setIsInviteFlow] = useState(false);
  const navigate = useNavigate();
  const { refreshProfile } = useAuth();

  useEffect(() => {
    // Détecter si l'utilisateur arrive via un lien d'invitation Supabase.
    // - Implicit flow : #access_token=...&type=invite  → capturé dans main.tsx
    // - PKCE flow     : ?code=...&type=invite           → capturé dans main.tsx
    // On lit sessionStorage (capturé avant que le SDK n'efface l'URL) et en
    // fallback les params encore présents (hash ou query).
    const storedType = sessionStorage.getItem('sb_url_type');
    const hashParams = new URLSearchParams(window.location.hash.slice(1));
    const queryParams = new URLSearchParams(window.location.search);
    const urlType = storedType
      || hashParams.get('type')
      || queryParams.get('type');

    if (urlType === 'invite') {
      setIsInviteFlow(true);
      sessionStorage.removeItem('sb_url_type'); // consommé, on nettoie
    }

    async function checkSession() {
      try {
        // En flow PKCE, le SDK échange le ?code= de façon asynchrone.
        // On réessaie une fois si la session n'est pas encore disponible.
        let session = (await supabase.auth.getSession()).data.session;

        if (!session) {
          await new Promise(resolve => setTimeout(resolve, 800));
          session = (await supabase.auth.getSession()).data.session;
        }

        if (session?.user) {
          setIsValidToken(true);
        } else {
          setIsValidToken(false);
          toast.error('Lien d\'activation invalide ou expiré');
          setTimeout(() => navigate('/reset-password'), 2000);
        }
      } catch (err) {
        console.error('Unexpected error:', err);
        setIsValidToken(false);
        toast.error('Une erreur inattendue est survenue');
        setTimeout(() => navigate('/reset-password'), 2000);
      }
    }

    checkSession();
  }, [navigate]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();

    const validation = validatePasswordStrength(password);

    if (!validation.isValid) {
      toast.error('Le mot de passe ne respecte pas tous les critères requis');
      return;
    }

    if (password !== confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }

    try {
      setLoading(true);

      const { error } = await supabase.auth.updateUser({
        password: password,
      });

      if (error) {
        if (error.message.includes('session')) {
          toast.error('Votre session a expiré. Veuillez redemander un lien de réinitialisation.');
          setTimeout(() => navigate('/reset-password'), 2000);
        } else {
          toast.error('Erreur lors de la mise à jour du mot de passe');
        }
        return;
      }

      if (isInviteFlow) {
        // Flux invitation : l'utilisateur est déjà connecté après avoir défini son mot de passe.
        // On force un refresh du profil pour s'assurer que le bon rôle est chargé avant
        // la redirection (évite les race conditions entre getSession et onAuthStateChange).
        toast.success('Bienvenue ! Votre compte est activé.');
        await refreshProfile();
        setTimeout(() => navigate('/dashboard'), 1200);
      } else {
        // Flux réinitialisation classique : déconnexion puis login.
        toast.success('Mot de passe mis à jour avec succès !');
        await supabase.auth.signOut();
        setTimeout(() => {
          navigate('/login', {
            state: { message: 'Votre mot de passe a été modifié. Vous pouvez maintenant vous connecter.' }
          });
        }, 1500);
      }

    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Une erreur inattendue est survenue');
    } finally {
      setLoading(false);
    }
  }

  if (isValidToken === null) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white mx-auto mb-4"></div>
          <p>Vérification...</p>
        </div>
      </div>
    );
  }

  if (isValidToken === false) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center">
        <div className="text-white text-center">
          <p>Redirection...</p>
        </div>
      </div>
    );
  }

  const validation = validatePasswordStrength(password);
  const passwordsMatch = password && confirmPassword && password === confirmPassword;
  const canSubmit = validation.isValid && passwordsMatch && !loading;

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
      <div className="absolute inset-0 bg-gradient-to-br from-blue-900/20 to-slate-900/20" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="sm:mx-auto sm:w-full sm:max-w-md relative z-10"
      >
        <div className="flex justify-center">
          <div className="bg-blue-600 p-3 rounded-full">
            <Lock className="h-8 w-8 text-white" />
          </div>
        </div>
        <h2 className="mt-6 text-center text-3xl font-extrabold text-white">
          {isInviteFlow ? 'Bienvenue !' : 'Créer un nouveau mot de passe'}
        </h2>
        <p className="mt-2 text-center text-sm text-slate-300">
          {isInviteFlow
            ? 'Définissez votre mot de passe pour accéder à votre compte.'
            : 'Choisissez un mot de passe fort et sécurisé'}
        </p>
      </motion.div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md relative z-10">
        <div className="bg-gradient-to-br from-slate-800/40 to-slate-900/40 backdrop-blur-xl border border-white/10 py-8 px-4 shadow-2xl rounded-2xl sm:px-10 hover:border-white/20 transition-all duration-300">
          <form className="space-y-6" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-slate-300">
                Nouveau mot de passe
              </label>
              <div className="mt-1 relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 bg-slate-800/50 border border-white/10 rounded-lg shadow-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Entrez votre nouveau mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-300"
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              <PasswordStrengthIndicator password={password} />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-slate-300">
                Confirmer le mot de passe
              </label>
              <div className="mt-1 relative">
                <input
                  id="confirmPassword"
                  name="confirmPassword"
                  type={showConfirmPassword ? 'text' : 'password'}
                  autoComplete="new-password"
                  required
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="appearance-none block w-full px-3 py-2 pr-10 bg-slate-800/50 border border-white/10 rounded-lg shadow-sm text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
                  placeholder="Confirmez votre mot de passe"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-300"
                >
                  {showConfirmPassword ? (
                    <EyeOff className="h-5 w-5" />
                  ) : (
                    <Eye className="h-5 w-5" />
                  )}
                </button>
              </div>
              {confirmPassword && (
                <div className="mt-2 flex items-center space-x-2">
                  {passwordsMatch ? (
                    <>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                      <span className="text-xs text-green-400">Les mots de passe correspondent</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="h-4 w-4 text-red-500" />
                      <span className="text-xs text-red-400">Les mots de passe ne correspondent pas</span>
                    </>
                  )}
                </div>
              )}
            </div>

            <div>
              <button
                type="submit"
                disabled={!canSubmit}
                className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-lg shadow-lg text-sm font-medium text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 hover:shadow-xl focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                    Mise à jour...
                  </>
                ) : (
                  <>
                    <Lock className="h-5 w-5 mr-2" />
                    {isInviteFlow ? 'Activer mon compte' : 'Mettre à jour le mot de passe'}
                  </>
                )}
              </button>
            </div>
          </form>

          <div className="mt-6 p-4 bg-blue-900/20 border border-blue-500/20 rounded-lg">
            <p className="text-xs text-slate-400 text-center">
              {isInviteFlow
                ? 'Après activation, vous serez redirigé vers votre tableau de bord.'
                : 'Après la mise à jour, vous serez déconnecté et devrez vous reconnecter avec votre nouveau mot de passe.'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function XCircle({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <circle cx="12" cy="12" r="10" strokeWidth="2" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 9l-6 6m0-6l6 6" />
    </svg>
  );
}
